import { useState, type FormEvent } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { AuthSplitLayout, AuthImagePanel, Button, Input } from '@discovr/ui';
import { WarningCircle } from '@discovr/ui/design-system/icons';
import { domainJoin, type BrandLookupResult } from '@discovr/api';

const joinLookupRoute = getRouteApi('/join');

const SIDE_PANEL = <AuthImagePanel />;

/**
 * Entry point for a visitor who wants to request to join a brand's team but
 * doesn't already have a brand-specific /join/{slug} link — resolves which
 * brand(s), if any, their email's domain is eligible for, then hands off to
 * the existing (OTP-protected) JoinPage flow. This page itself never sends
 * an OTP or creates anything; lookupBrands is read-only.
 */
export default function JoinLookupPage() {
  const navigate = useNavigate();
  const { email: initialEmail } = joinLookupRoute.useSearch();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<BrandLookupResult[] | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || pending) return;
    setPending(true);
    setError(null);
    setMatches(null);
    try {
      const results = await domainJoin.lookupBrands(email.trim());
      const onlyMatch = results.length === 1 ? results[0] : undefined;
      if (onlyMatch) {
        void navigate({
          to: '/join/$brandSlug',
          params: { brandSlug: onlyMatch.slug },
          search: { email: email.trim() },
        });
        return;
      }
      setMatches(results);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  if (matches && matches.length > 1) {
    const trimmedEmail = email.trim();
    return (
      <AuthSplitLayout backTo="/login" headline="Choose a workspace." sidePanel={SIDE_PANEL}>
        {/* role="status" announces this state swap to assistive tech — no route
            change happens, so without it a screen-reader user gets no signal a
            picker appeared (matches JoinPage.tsx's identical "submitted" swap). */}
        <p role="status" className="mb-4 text-body-sm-regular text-muted-foreground">
          More than one workspace allows {trimmedEmail} to request access. Pick one to continue.
        </p>
        <ul className="flex list-none flex-col gap-2 p-0">
          {matches.map((b) => (
            <li key={b.slug}>
              <Link
                to="/join/$brandSlug"
                params={{ brandSlug: b.slug }}
                search={{ email: trimmedEmail }}
                className="block rounded-lg border border-border bg-card px-4 py-3 text-body-sm-medium text-foreground hover:bg-muted"
              >
                {b.name}
              </Link>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="link"
          onClick={() => setMatches(null)}
          className="mt-4 h-auto w-full p-0 text-foreground"
        >
          Use a different email
        </Button>
      </AuthSplitLayout>
    );
  }

  if (matches && matches.length === 0) {
    return (
      <AuthSplitLayout
        backTo="/login"
        headline="No workspace found."
        // role="status" — see the picker branch above for why this matters here too.
        subheadline={`No workspace found for ${email.trim()}. Check the email, or ask your team's admin to invite you directly.`}
        sidePanel={SIDE_PANEL}
      >
        <p role="status" className="sr-only">
          No workspace found for {email.trim()}.
        </p>
        <Button type="button" variant="outline" onClick={() => setMatches(null)} className="h-11 w-full rounded-full">
          Try a different email
        </Button>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout
      backTo="/login"
      headline="Request access."
      subheadline="Enter your work email and we'll find your team's workspace."
      sidePanel={SIDE_PANEL}
    >
      <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="join-lookup-email" className="text-body-sm-medium text-foreground">
            Work email
          </label>
          <Input
            id="join-lookup-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            required
            autoFocus
            className="h-11 rounded-lg border-border bg-muted focus:bg-card"
          />
        </div>
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700"
          >
            <WarningCircle size={15} className="mt-px shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <Button
          type="submit"
          loading={pending}
          disabled={!email.trim()}
          className="h-11 w-full rounded-full"
        >
          Continue
        </Button>
      </form>
    </AuthSplitLayout>
  );
}
